/**
 * Normalización de unidades para comparación entre concurso y catálogo.
 *
 * Hay dos niveles:
 *   1. normalizeUnit(u): canónica solo nomenclatura (mayúsculas, sin
 *      puntos/espacios, ² → 2). Útil cuando no quieres tratar aliases.
 *   2. canonicalUnit(u): además mapea aliases que significan la MISMA unidad
 *      (ej. "m" / "ml" / "MTS" → metro). NO mapea conversiones de escala
 *      (ton ≠ kg, km ≠ m); esas requieren conversión numérica.
 *   3. unitsAreEquivalent(a, b): true si comparten unidad canónica.
 *
 * Convenciones:
 * - "m" y "ml" son la misma unidad (metro lineal).
 * - "ton" y "t" son la misma unidad (tonelada). NO equivalente a "kg".
 * - "lt" se interpreta como litro (es la abreviatura más común en catálogos
 *   de obra; "lote" se escribe completo). Si tu catálogo usa "lt" como lote,
 *   ajusta este map.
 */

const UNIT_ALIASES: Record<string, string> = {
  // Metro lineal
  M: 'M', ML: 'M', MTS: 'M', MTRS: 'M', METRO: 'M', METROS: 'M',
  // Metro cuadrado
  M2: 'M2',
  // Metro cúbico
  M3: 'M3',
  // Kilómetro (NO se mapea a metro: distinta escala)
  KM: 'KM',
  // Pieza
  PZA: 'PZA', PZ: 'PZA', PIEZA: 'PZA', PIEZAS: 'PZA', PZS: 'PZA',
  // Juego
  JGO: 'JGO', JTO: 'JGO', JUEGO: 'JGO', JUEGOS: 'JGO',
  // Lote / global
  LOTE: 'LOTE', LOT: 'LOTE',
  GLB: 'GLB', GBL: 'GLB', GLOBAL: 'GLB',
  // Kilogramo
  KG: 'KG', KGS: 'KG', KILO: 'KG', KILOS: 'KG', KILOGRAMO: 'KG',
  // Tonelada (NO equivalente a kg)
  TON: 'TON', TONS: 'TON', T: 'TON', TONELADA: 'TON', TONELADAS: 'TON',
  // Hora
  HR: 'HR', HRS: 'HR', HORA: 'HR', HORAS: 'HR', H: 'HR',
  // Jornada
  JOR: 'JOR', JORNADA: 'JOR',
  // Litro
  L: 'L', LT: 'L', LTS: 'L', LITRO: 'L', LITROS: 'L',
  // Galón
  GAL: 'GAL', GALON: 'GAL', GALONES: 'GAL',
  // Caja
  CAJ: 'CAJA', CAJA: 'CAJA', CAJAS: 'CAJA',
  // Bote / cubeta (no se unifican: tamaños distintos)
  BOTE: 'BOTE', BOTES: 'BOTE',
  CUBETA: 'CUBETA', CUBETAS: 'CUBETA',
  // Saco / costal
  SAC: 'SACO', SACO: 'SACO', SACOS: 'SACO', COSTAL: 'SACO', COSTALES: 'SACO',
  // Rollo
  ROLL: 'ROLLO', ROLLO: 'ROLLO', ROLLOS: 'ROLLO',
  // Millar
  MILL: 'MILLAR', MILLAR: 'MILLAR', MIL: 'MILLAR',
};

/** Limpia formato textual: mayúsculas, sin puntos ni espacios, ² → 2, ³ → 3. */
export function normalizeUnit(u: string | null | undefined): string {
  return (u ?? '')
    .toUpperCase()
    .replace(/[.\s]/g, '')
    .replace('²', '2')
    .replace('³', '3');
}

/** Devuelve la unidad canónica resolviendo aliases (m → M, ml → M, ton → TON, etc.). */
export function canonicalUnit(u: string | null | undefined): string {
  const norm = normalizeUnit(u);
  if (!norm) return '';
  return UNIT_ALIASES[norm] ?? norm;
}

/** True si ambas unidades resuelven a la misma unidad canónica. */
export function unitsAreEquivalent(a: string | null | undefined, b: string | null | undefined): boolean {
  const ca = canonicalUnit(a);
  const cb = canonicalUnit(b);
  if (!ca || !cb) return false;
  return ca === cb;
}
